import produce from 'immer';
import React, {useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import {MethodUpdater as LegacyMethodUpdater} from 'procedures/design/LegacyProcedureEditor';
import MethodEditor from 'procedures/design/method-config/MethodEditor';
import {Method} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import {isMethodNode, isNodeValidInProcedureFlowchart} from 'procedures/processes/MethodNodeUtils';
import {
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
  TaskNodeDetailsSidepanelContentProps,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {getApplicationDataSafely} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {ProcedurePauseCondition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';
import useOnMount from 'ui-library/hooks/useOnMount';

import MethodSidepanelContext from './MethodSidepanelContext';

export default function MethodSidepanelEditor(
  props: TaskNodeDetailsSidepanelContentProps<Method>
): JSX.Element {
  const {procedureMetadata} = ProcedurePageContext.useContext();

  const {selectedNodeTaskDefinitionVersionSpec, setIsAutosaverPaused} = props;
  const taskDefinitionVersion = selectedNodeTaskDefinitionVersionSpec.newProcessTaskDefinitionVersion;

  useOnMount(() => {
    assert(
      Object.values(props.flowchart.nodes).every((node) => isNodeValidInProcedureFlowchart(node)),
      'MethodSidepanelEditor requires a flowchart of nodes that can be used in Procedures'
    );
  });

  const onUpdateTaskDefinitionVersion = props.onUpdateTaskDefinitionVersion;
  const {applicationData: method} = taskDefinitionVersion;
  assert(method != null, 'MethodSidepanelEditor requires a Method task definition version');

  const onChangeMethod = useCallback(
    (_cid: string, methodUpdater: LegacyMethodUpdater): void => {
      onUpdateTaskDefinitionVersion(
        (currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart<Method>) => {
          const currentNodeSpec = currentFlowchart.nodes[props.selectedNodeId];

          assert(
            currentNodeSpec != null,
            `Node ${selectedNodeTaskDefinitionVersionSpec.clientId} not found in current flowchart`
          );

          const currentMethodData = getApplicationDataSafely<Method>(currentNodeSpec);
          const newMethodData = methodUpdater(currentMethodData);

          return produce(currentNodeSpec, (draftVersion) => {
            draftVersion.newProcessTaskDefinitionVersion.applicationData = newMethodData;
          });
        },
        false
      );
    },
    [selectedNodeTaskDefinitionVersionSpec, onUpdateTaskDefinitionVersion, props.selectedNodeId]
  );

  // TODO(BNCH-133735): Move this into a hook which will delegate to one of the two versions of saveImmediately
  const customSaveImmediately = useCallback(
    async (methodData: Method): Promise<void> => {
      await onUpdateTaskDefinitionVersion(
        (currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart<Method>) => {
          const currentNodeSpec = currentFlowchart.nodes[props.selectedNodeId];

          assert(currentNodeSpec != null, `Node ${props.selectedNodeId} not found in current flowchart`);

          return produce(currentNodeSpec, (draftVersion) => {
            draftVersion.newProcessTaskDefinitionVersion.applicationData = methodData;
          });
        },
        true
      );
    },
    [onUpdateTaskDefinitionVersion, props.selectedNodeId]
  );

  const customSetAutosaverPaused = useCallback(
    (condition: ProcedurePauseCondition, value: boolean) => {
      setIsAutosaverPaused(condition, value);
    },
    [setIsAutosaverPaused]
  );

  const allMethodNames = Object.values(props.flowchart.nodes)
    .filter((node) => isMethodNode(node))
    .map((node) => {
      const applicationData = getApplicationDataSafely<Method>(node);
      return {cid: applicationData.cid, name: applicationData.name};
    });

  const contextValue = useMemo(
    () => ({saveImmediately: customSaveImmediately, pauseAutosaver: customSetAutosaverPaused}),
    [customSaveImmediately, customSetAutosaverPaused]
  );

  return (
    <MethodSidepanelContext.Provider value={contextValue}>
      <MethodEditor
        // Rebuild the method editor if the selected node changes
        key={props.selectedNodeTaskDefinitionVersionSpec.clientId}
        selectedMethod={method}
        onChangeMethod={onChangeMethod}
        // TODO: these 3 props need proper hydration via the workflows platform
        procedureTemplateCollectionId={procedureMetadata.templateCollectionOwner.id}
        isProcedureReadOnly={Boolean(props.locked)}
        isProcedureArchived={false}
        allMethodNames={allMethodNames}
      />
    </MethodSidepanelContext.Provider>
  );
}
